import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

// The small chart family of the dashboard and the Cockpit: one brand ramp for magnitude, status colours only for states
// (good / warning / serious / critical), thin marks, a 2 px surface gap between fills, labels in text ink — never the series colour.
// Every chart has its legend with the figures (so nothing depends on colour or hover alone).
export const RAMP = ["#083346", "#0f5f7a", "#2a8aa6", "#6bb5c9", "#a9d4e0", "#d5eaf0"];
export const STATUS = { good: "#16a34a", warning: "#d97706", serious: "#ea580c", critical: "#dc2626", neutral: "#94a3b8" };

const total = (rows) => rows.reduce((sum, row) => sum + (row.value || 0), 0);
const percent = (value, sum) => (sum > 0 ? `${Math.round((value / sum) * 100)} %` : "0 %");

/** Ranked horizontal bars: the figure in text ink at the end, the bar thin and rounded at the data end. */
export function HBars({ rows, testid, format = (v) => v }) {
  const shown = rows.filter((row) => row.value > 0 || row.keep);
  if (!shown.length) return null;
  const max = Math.max(...shown.map((row) => row.value), 1);
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {shown.map((row, i) => (
        <li key={row.id} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 text-sm" data-testid={testid && `${testid}-${row.id}`}>
          <span className="truncate text-slate-700">{row.label}</span>
          <span className="h-2 rounded-full bg-slate-100" aria-hidden="true">
            <span className="block h-2 rounded-full" style={{ width: `${Math.max((row.value / max) * 100, row.value ? 3 : 0)}%`, background: row.color || RAMP[Math.min(i, RAMP.length - 1)] }} />
          </span>
          <span className="font-semibold tabular-nums text-slate-900">{format(row.value)}</span>
        </li>
      ))}
    </ul>
  );
}

/** A donut with its total in the middle and a legend (label, figure, share). */
export function Donut({ rows, centerLabel, testid, size = 132 }) {
  const shown = rows.filter((row) => row.value > 0);
  const sum = total(shown);
  if (!sum) return null;
  return (
    <div className="flex flex-wrap items-center gap-4" data-testid={testid}>
      <div className="relative shrink-0" style={{ width: size, height: size }} role="img"
        aria-label={`${centerLabel || "Total"} : ${sum}. ${shown.map((r) => `${r.label} ${r.value}`).join(", ")}`}>
        <PieChart width={size} height={size}>
          <Pie data={shown} dataKey="value" nameKey="label" innerRadius={size * 0.34} outerRadius={size * 0.48} paddingAngle={shown.length > 1 ? 2 : 0} stroke="none" isAnimationActive={false}>
            {shown.map((row, i) => <Cell key={row.id} fill={row.color || RAMP[Math.min(i, RAMP.length - 1)]} />)}
          </Pie>
          <Tooltip formatter={(value, name) => [`${value} (${percent(value, sum)})`, name]} />
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-xl font-bold tabular-nums text-slate-900">{sum}</span>
          {centerLabel && <span className="text-2xs text-slate-500">{centerLabel}</span>}
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1 text-sm">
        {shown.map((row, i) => (
          <li key={row.id} className="flex items-center gap-2" data-testid={testid && `${testid}-${row.id}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: row.color || RAMP[Math.min(i, RAMP.length - 1)] }} aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-slate-700">{row.label}</span>
            <span className="font-semibold tabular-nums text-slate-900">{row.value}</span>
            <span className="w-10 text-right text-xs tabular-nums text-slate-500">{percent(row.value, sum)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One stacked bar for a composition of a few states (risk levels, health...), with the legend under it. */
export function StackedBar({ rows, testid }) {
  const shown = rows.filter((row) => row.value > 0);
  const sum = total(shown);
  if (!sum) return null;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full" role="img" aria-label={shown.map((r) => `${r.label} ${r.value}`).join(", ")}>
        {shown.map((row, i) => (
          <span key={row.id} className="h-full first:rounded-l-full last:rounded-r-full" title={`${row.label} : ${row.value}`}
            style={{ flex: row.value, background: row.color || RAMP[Math.min(i, RAMP.length - 1)] }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {shown.map((row, i) => (
          <li key={row.id} className="inline-flex items-center gap-1.5" data-testid={testid && `${testid}-${row.id}`}>
            <span className="h-2 w-2 rounded-sm" style={{ background: row.color || RAMP[Math.min(i, RAMP.length - 1)] }} aria-hidden="true" />
            {row.label} <strong className="tabular-nums text-slate-900">{row.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A small area chart for a series over time (points: {label, value}); hover shows the exact value. */
export function Spark({ points, color = RAMP[1], height = 56, format = (v) => v, testid }) {
  if (!points || points.length < 2) return null;
  const id = `spark-${(testid || "x").replace(/[^a-z0-9]/gi, "")}`;
  return (
    <div style={{ height }} className="min-w-0" data-testid={testid} role="img" aria-label={`Évolution : ${points.map((p) => `${p.label} ${format(p.value)}`).join(", ")}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" hide />
          <YAxis hide domain={[0, "dataMax"]} />
          <Tooltip formatter={(value) => [format(value), ""]} labelFormatter={(label) => label} separator="" />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${id})`} dot={false} activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const weekText = (week) => new Date(`${week}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

/**
 * Twelve weeks of a count. One series = bars; two series = overlapping areas (e.g. created vs resolved), each with its legend entry.
 * `series`: [{ key, label, color, points: [{ week: "AAAA-MM-JJ", count }] }].
 */
export function WeekChart({ series, height = 120, testid }) {
  const usable = (series || []).filter((s) => s.points?.length);
  if (!usable.length) return null;
  const data = usable[0].points.map((point, i) => ({ week: point.week, ...Object.fromEntries(usable.map((s) => [s.key, s.points[i]?.count ?? 0])) }));
  const sum = usable.flatMap((s) => s.points).reduce((acc, p) => acc + p.count, 0);
  const common = { data, margin: { top: 6, right: 4, bottom: 0, left: -18 } };
  const axes = (
    <>
      <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
      <XAxis dataKey="week" tickFormatter={weekText} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={34} />
      <Tooltip labelFormatter={(week) => `Semaine du ${weekText(week)}`} formatter={(value, name) => [value, usable.find((s) => s.key === name)?.label || name]} />
    </>
  );
  return (
    <div data-testid={testid} className="space-y-1">
      <div style={{ height }} role="img" aria-label={`${usable.map((s) => s.label).join(" et ")} par semaine, 12 dernières semaines : ${sum} au total`}>
        <ResponsiveContainer width="100%" height="100%">
          {usable.length === 1 ? (
            <BarChart {...common}>
              {axes}
              <Bar dataKey={usable[0].key} fill={usable[0].color || RAMP[1]} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          ) : (
            <AreaChart {...common}>
              {axes}
              {usable.map((s) => <Area key={s.key} type="monotone" dataKey={s.key} stroke={s.color} fill={s.color} fillOpacity={0.12} strokeWidth={2} dot={false} isAnimationActive={false} />)}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
      {usable.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          {usable.map((s) => (
            <li key={s.key} className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: s.color }} aria-hidden="true" />{s.label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
